<template>
  <!-- Table Container Card -->
  <b-card no-body>

    <div class="m-2">
      <b-row>
        <!-- Per Page -->
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <TableEntries
            v-model="perPage"
            :options="perPageOptions"
          />
          <b-dropdown
            v-if="getUserPermission('EXPORT_ECHANNEL_REPORT')"
            id="dropdown-1"
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            :text="$t('Export')"
            variant="primary"
          >
            <b-dropdown-item @click="csvExport">
              {{ $t('CSV') }}
            </b-dropdown-item>
            <b-dropdown-item @click="excelExport">
              {{ $t('Excel') }}
            </b-dropdown-item>
          </b-dropdown>
        </b-col>
        <!-- Export -->
        <b-col
          cols="12"
          md="6"
        >
          <div class="d-flex align-items-center justify-content-lg-end">
            <Button
              :label="$t('Filter')"
              size="md"
              icon="FilterIcon"
              show-icon
              class="mr-1"
              @click="showModal('modal-filter')"
            />
            <Button
              :label="$t('Reset Filter')"
              size="md"
              icon="RotateCwIcon"
              show-icon
              @click="resetFilter"
            />
          </div>
        </b-col>
      </b-row>
    </div>

    <b-table
      ref="eChannelTableRef"
      :items="reportData"
      :busy="isBusy"
      :per-page="pageSize"
      :current-page="currentPage"
      responsive
      :fields="translatedTableHeaders"
      primary-key="id"
      show-empty
      :empty-text="$t('No records available')"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      class="position-relative"
    >

      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            class="align-middle"
            variant="primary"
          />
        </div>
      </template>

      <template #cell(SN)="data">
        {{ getOverallIndex(data.index) }}
      </template>

      <template #cell(financialDate)="data">
        <p class="mb-0">
          {{ data.value ? toDateWithYear(data.value) : '' }}
        </p>
      </template>
      <template #cell(logDate)="data">
        <p class="mb-0">
          {{ data.value ? toDateWithCustomTime(data.value) : '' }}
        </p>
      </template>

      <template #cell(f4_TransactionAmount)="data">
        <span class="text-nowrap">
          {{ data.value ? toBalance(data.value) : '' }}
        </span>
      </template>

      <!-- Column: account number -->
      <template #cell(accountNumber)="data">
        {{ data.value }}
      </template>

      <!-- Column: Account Name -->
      <template #cell(accountName)="data">
        {{ data.value }}
      </template>

      <template #cell(debit)="data">
        <span class="text-nowrap">
          {{ data.value ? toBalance(data.value) : 0 }}
        </span>
      </template>

      <template #cell(credit)="data">
        <span class="text-nowrap">
          {{ data.value ? toBalance(data.value) : 0 }}
        </span>
      </template>
    </b-table>

    <TablePagination
      v-model="currentPage"
      :from="from"
      :to="to"
      :total-rows="totalEntries"
      :per-page="perPage"
    />

    <!-- modal -->
    <b-modal
      id="modal-filter"
      ref="my-modal"
      :ok-title="$t('Apply')"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      centered
      size="lg"
      no-close-on-backdrop
      :title="$t('Filters')"
      modal-footer
      @ok="handleSubmit"
      @cancel="resetFilters"
    >

      <validation-observer ref="eChannelTransactionRules">
        <b-form>
          <b-row class="match-height">
            <b-col md="6">
              <InputField
                id="account-number"
                v-model="filter.accountNumber"
                :label="$t('Account Number')"
                :placeholder="$t('Enter Account Number')"
                type="text"
              />
            </b-col>
            <b-col md="6">
              <InputField
                id="terminal-id"
                v-model="filter.terminalID"
                :label="$t('Terminal ID')"
                :placeholder="$t('Enter Terminal ID')"
                type="text"
              />
            </b-col>
            <!-- Account Number -->
            <b-col md="6">
              <InputField
                id="stan"
                v-model="filter.stan"
                :label="$t('STAN')"
                :placeholder="$t('Enter STAN')"
                type="text"
              />
            </b-col>
            <!-- STAN -->
            <b-col md="6">
              <InputField
                id="maskedPAN"
                v-model="filter.maskedPAN"
                :label="$t('Masked PAN')"
                :placeholder="$t('Enter Masked PAN')"
                :error-name="$t('Masked PAN')"
                type="text"
                maxlength="16"
                @input="onMaskedPanInput"
              />
            </b-col>
            <!-- Start Date -->
            <b-col md="6">
              <DatePickerField
                id="start-date"
                v-model="filter.logDateFrom"
                :min-date="new Date()"
                :label="$t('Transaction Date From')"
                :config="datePickerConfig"
                :placeholder="$t('Transaction Date From')"
              />
            </b-col>
            <!-- Start Date -->
            <!-- End Date -->
            <b-col md="6">
              <DatePickerField
                id="end-date"
                v-model="filter.logDateTo"
                :min-date="new Date()"
                :label="$t('Transaction Date To')"
                :config="endDatePickerConfig"
                :placeholder="$t('Transaction Date To')"
              />
            </b-col>
            <!-- End Date -->
          </b-row>

        </b-form>
      </validation-observer>

      <template
        v-if="isReadonly"
        #modal-footer="{ }"
      >
        <div />
      </template>
    </b-modal>
    <!-- modal -->
  </b-card>

</template>

<script>
import {
  BCard, BRow, BCol, BTable, BSpinner, BForm,
  BModal, BDropdown, BDropdownItem,
} from 'bootstrap-vue'
import { ValidationObserver } from 'vee-validate'
import { required } from '@validations'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { tableMixins } from '@core/mixins/table'
import { permissionMixin } from '@core/mixins/permission'
import { modalMixins } from '@core/mixins/modal'
import { mapGetters, mapActions } from 'vuex'
import Ripple from 'vue-ripple-directive'
import { ISOformatMaskedPan } from '@/helpers/formatMaskedPan'
import {
  toBalance, toFormatAmount, formatAmount,
} from '@/helpers/formatAmount'
import TablePagination from '@/views/components/table/TablePagination.vue'
import TableEntries from '@/views/components/table/TableEntries.vue'
import InputField from '../../../../forms/InputField.vue'
import Button from '../../../../components/button/Button.vue'
import toNumber from '../../../../../helpers/toNumber'
import DatePickerField from '../../../../forms/DatePickerField.vue'
import {
  toDateWithYear, toDateWithCustomTime,
} from '@/helpers/formatDate'
import { eChannelsPermission, reportPermissions } from '../../../../../helpers/permissions'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    Button,
    BSpinner,
    BTable,
    TablePagination,
    TableEntries,
    InputField,
    BModal,
    BDropdown,
    BDropdownItem,
    BForm,
    ValidationObserver,
    DatePickerField,
  },
  directives: {
    Ripple,
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin],
  data() {
    return {
      toBalance,
      toFormatAmount,
      ISOformatMaskedPan,
      formatAmount,
      toDateWithYear,
      toDateWithCustomTime,
      query: '',
      statusToggleLabel: '',
      isReadonly: false,
      buttonLabel: this.$t('Save'),
      isEditable: false,
      isBusy: false,
      required,
      modalTitle: this.$t('E-Channel Transactions Report'),
      sortBy: 'name',
      sortDesc: false,
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'logDate', sortable: true, label: 'Transaction Date' },
        { key: 'f4_TransactionAmount', sortable: true, label: 'Transaction Amount' },
        { key: 'f37_RetrievalReferenceNumber', sortable: true, label: 'Retrieval Reference Number' },
        { key: 'instrumentNumber', sortable: true, label: 'Instrument No.' },
        { key: 'acquiringInstitutionIDCode', sortable: true, label: 'Acquiring Institution ID Code' },
        { key: 'f43_CardAcceptorNameLocation', sortable: true, label: 'Card Acceptor Name Location' },
        { key: 'f18_MerchantType', sortable: true, label: 'Merchant Type' },
        { key: 'transactionChannel', sortable: true, label: 'Transaction Channel' },
        { key: 'f11_SystemTraceAuditNumber', sortable: true, label: 'Stan' },
        { key: 'f102_AccountIdentification1', sortable: true, label: 'Account1' },
        { key: 'f103_AccountIdentification2', sortable: true, label: 'Account2' },
        { key: 'f2_HiddenPAN', sortable: true, label: 'Hidden Pan' },
        { key: 'f41_CardAcceptorTerminalID', sortable: true, label: 'Terminal ID' },
      ],

      filter: {
        stan: '',
        maskedPAN: '',
        logDateFrom: null,
        logDateTo: null,
        terminalID: '',
        accountNumber: '',
      },
      transactionChannelOptions: [
        { value: 'ATM', label: 'ATM' },
        { value: 'POS', label: 'POS' },
        { value: 'Web', label: 'Web' },
        { value: 'Mobile', label: 'Mobile' },
        { value: 'OpenAPI', label: 'Open API' },
        { value: 'Transfers_NIPInward', label: 'NIP Inward Transfer' },
        { value: 'InBranch', label: 'In-Branch' },
        { value: 'Transfers_ISWInward', label: 'ISW Inward Transfer' },
      ],
      datePickerConfig: {
        dateFormat: 'Y-m-d',
        maxDate: 'today',
        defaultDate: '',
        ariaDateFormat: 'Y-m-d',
      },
      reportData: [],
      allSearchedData: [],
      branchRefCode: '',
    }
  },
  computed: {
    ...mapGetters({
      branchOptions: 'operations/branchOptions',
      userDetails: 'auth/getCurrentUser',
    }),
    translatedTableHeaders() {
      return this.tableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
    translatedTransactionChannelOptions() {
      return this.transactionChannelOptions.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.value }))
    },
    endDatePickerConfig() {
      return {
        ...this.datePickerConfig,
        minDate: this.filter.logDateFrom ? new Date(this.filter.logDateFrom) : new Date(),
      }
    },
  },
  watch: {
    currentPage(newValue) {
      this.currentPage = newValue
      this.fetchIsoCardTransactions()
    },
    perPage(newValue) {
      this.perPage = newValue
      this.fetchIsoCardTransactions()
    },
  },
  mounted() {
    this.fetchIsoCardTransactions()
  },
  methods: {
    ...mapActions({
      getIsoCardTransactionReport: 'eChannels/getIsoCardTransactionReport',
      downloadISOCardTransactionReport: 'reports/downloadISOCardTransactionReport',
      getBranchByCode: 'operations/getBranchByCode',
    }),

    onMaskedPanInput(value) {
      const formattedValue = this.ISOformatMaskedPan(value)
      // Update the filter value
      this.filter.maskedPAN = formattedValue
      // If the formatted value is different, update it
      if (value !== formattedValue) {
        this.$nextTick(() => {
          this.filter.maskedPAN = formattedValue
        })
      }
    },
    resetFilters() {
      this.filter = { ...null }
    },
    resetFilter() {
      this.filter = { ...null }
      this.fetchIsoCardTransactions()
    },

    async getBranchValue(dataValue) {
      if (dataValue !== null) {
        try {
          const response = await this.getBranchByCode(dataValue)
          const { status, data } = response
          if (status === 200 && data) {
            this.branchRefCode = data
          }
        } catch (error) {
          this.alertError({ text: error })
        } finally {
          this.isBusy = false
        }
      }
    },
    async handleSubmit(bvModalEvt) {
      bvModalEvt.preventDefault()
      this.$refs.eChannelTransactionRules.validate().then(async success => {
        if (success) {
          this.fetchIsoCardTransactions()
        }
      })
    },
    async fetchIsoCardTransactions() {
      try {
        this.hideModal('modal-filter')
        this.isBusy = true
        const response = await this.getIsoCardTransactionReport({
          search: this.filter,
          page: this.currentPage,
          limit: this.perPage,
          permission: eChannelsPermission.VIEW_CARD_TRANSACTION_REPORT,
        })
        const { data, status } = response
        if (status === 200 && data.data) {
          this.isBusy = false
          this.reportData = data.data
          this.totalCount = data.recordCount
        } else {
          this.reportData = []
          this.totalCount = 0
        }
      } catch (error) {
        error.response.data.errors.forEach(responseError => {
          const requestText = this.$t('Your request to download E-Channel Transaction Report failed. Reason:')
          this.alertError({ text: `${requestText} ${responseError.errorMessage}` })
        })
      } finally {
        this.isBusy = false
      }
    },
    async csvExport() {
      try {
        this.isBusy = true
        const response = await this.downloadISOCardTransactionReport({
          search: this.filter,
          page: this.currentPage,
          limit: this.perPage,
          fileExportFormat: 1,
          permission: reportPermissions.EXPORT_ECHANNEL_REPORT,
        })
        if (response.status === 200) {
          this.alertSuccessCustom({
            text: this.$t('Your download is in progress and might take a little longer. Go to your'),
            link: {
              text: this.$t('Download Manager'),
              route: { name: 'download-manager' },
            },
            afterLinkText: this.$t('to access the completed file.'),
          })
        } else if (response.status === 504) {
          this.alertSuccessCustom({
            text: this.$t('Your download request is still processing, keep checking the'),
            link: {
              text: this.$t('Download Manager'),
              route: { name: 'download-manager' },
            },
            afterLinkText: this.$t('for status.'),
          })
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },
    async excelExport() {
      try {
        this.isBusy = true
        const response = await this.downloadISOCardTransactionReport({
          search: this.filter,
          page: this.currentPage,
          limit: this.perPage,
          fileExportFormat: 0,
          permission: reportPermissions.EXPORT_ECHANNEL_REPORT,
        })
        if (response.status === 200) {
          this.alertSuccessCustom({
            text: this.$t('Your download is in progress and might take a little longer. Go to your'),
            link: {
              text: this.$t('Download Manager'),
              route: { name: 'download-manager' },
            },
            afterLinkText: this.$t('to access the completed file.'),
          })
        } else if (response.status === 504) {
          this.alertSuccessCustom({
            text: this.$t('Your download request is still processing, keep checking the'),
            link: {
              text: this.$t('Download Manager'),
              route: { name: 'download-manager' },
            },
            afterLinkText: this.$t('for status.'),
          })
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },
    formatNumber: toNumber,
  },
}
</script>

<style lang="scss" scoped>
    .per-page-selector {
        width: 90px;
    }

    .invoice-filter-select {
        min-width: 190px;

        ::v-deep .vs__selected-options{
            flex-wrap: nowrap;
        }

        ::v-deep .vs__selected {
            width: 100px;
        }
    }
</style>

<style lang="scss">
    @import '@core/scss/vue/libs/vue-select.scss';
</style>
